/**
 * O domínio da fila de erro de e-mail (`NR-141`) no front, em pt-BR.
 *
 * O contrato vem em inglês (`EmailFailureResponse`) e é traduzido UMA vez, em
 * `mapeadores.ts`, logo depois do fetch — a tela não conhece o nome do fio.
 *
 * Aqui não existe o detalhe da exceção nem o identificador do provedor: a
 * tabela guarda dado de paciente e a API não os devolve (`TP-036`, `D3`).
 */

import type { Instante } from '../../../shared/dominio/datas';

export type EtapaDaFalha = 'coleta' | 'triagem';

export const ETAPAS_DA_FALHA: readonly EtapaDaFalha[] = ['coleta', 'triagem'];

/**
 * Etapa que o front não conhece (a API ganhou uma e a tela ainda não). A linha
 * aparece mesmo assim: numa fila de erro, esconder a falha pelo rótulo seria
 * pior do que mostrá-la sem saber nomear a etapa.
 */
export type EtapaConhecidaOuNao = EtapaDaFalha | 'desconhecida';

export const ROTULOS_DE_ETAPA: Record<EtapaConhecidaOuNao, string> = {
  coleta: 'Coleta',
  triagem: 'Triagem',
  desconhecida: 'Etapa desconhecida',
};

export interface FalhaDeEmail {
  readonly id: string;
  readonly etapa: EtapaConhecidaOuNao;
  /** Chave opaca da caixa, nunca o endereço. */
  readonly caixa: string;
  /** O nome do tipo da exceção ou uma constante — nunca a mensagem bruta. */
  readonly motivo: string;
  readonly tentativas: number;
  readonly primeiraEm: Instante;
  readonly ultimaEm: Instante;
  /** Estourou o teto de tentativas: segue na fila, mas o reprocesso não a tenta mais. */
  readonly esgotada: boolean;
  /** Só a falha de coleta que ainda não esgotou. */
  readonly reprocessavel: boolean;
  /** Só a falha de triagem na caixa em que a tela refaz a triagem. Não depende de esgotamento. */
  readonly triavel: boolean;
}

export interface PaginaDeFalhas {
  readonly itens: readonly FalhaDeEmail[];
  readonly total: number;
}

/** O que uma rodada de triagem da caixa fez. As contagens são da caixa, não só das falhas. */
export interface ResultadoDaTriagem {
  /** E-mails sem ticket que a rodada tentou atribuir. */
  readonly vistos: number;
  readonly abertos: number;
  readonly vinculados: number;
  readonly reabertos: number;
  readonly falharam: number;
  /** Sobrou e-mail sem ticket que esta chamada não tentou (teto de itens ou prazo). */
  readonly aindaHaFila: boolean;
}

export interface ResultadoDoReprocesso {
  readonly tentadas: number;
  readonly reprocessadas: number;
  readonly naoEncontradas: number;
  readonly falharam: number;
  /** Sobrou fila que esta chamada não tentou (teto de itens ou prazo). */
  readonly aindaHaFila: boolean;
}
